// ===========================================
// Invoice Settings Tab
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Button,
  Stack,
  Typography,
  Alert,
  Divider,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as ResetIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  useInvoiceSettings,
  useUpdateInvoiceSettings,
  useResetInvoiceSettings,
} from '../../../hooks/useAdminSettings';
import type {
  TaxType,
  TaxMode,
  LineItemGrouping,
  LateFeeType,
  InvoiceSettingsInput,
  InvoiceSettingsResponse,
} from '../../../types/adminSettings.types';
import ReminderStepsCard from './ReminderStepsCard';
import LateFeesCard from './LateFeesCard';
import { classifyLateFeeChange, LATE_FEE_POLICY_KEYS, type LateFeePolicyFields } from '../../../utils/lateFeePolicyChange';
import { describeLateFeeChange, type LateFeeChangeDescription } from '../../../utils/describeLateFeeChange';
import { apiErrorCode, apiErrorMessage } from '../../../utils/apiError';
import { schoolTodayUtcMidnight } from '../../../utils/dateUtils';
import { useSchoolTimezone } from '../../../contexts/TenantContext';

// ===========================================
// TYPES
// ===========================================

interface InvoiceSettingsFormData {
  prefix: string;
  taxType: TaxType;
  taxName: string;
  taxRate: number;
  taxMode: TaxMode;
  taxNumber: string;
  defaultDueDays: number;
  // Payment reminders are a list of steps now (ReminderStepsCard owns them); only the billing contact stays here.
  /** '' in the form; saved as null ("use the school email") */
  billingContactEmail: string;
  footerText: string;
  invoiceLineItemGrouping: LineItemGrouping;
  // Late fees (#372). A blank optional number is '' in the form and null on the server.
  lateFeeEnabled: boolean;
  lateFeeType: LateFeeType;
  lateFeeAmount: number | '';
  lateFeePercent: number | '';
  lateFeePerFeeCap: number | '';
  lateFeeFirstDays: number | '';
  lateFeeRepeatEnabled: boolean;
  lateFeeRepeatFirstDays: number | '';
  lateFeeRepeatEveryDays: number | '';
  lateFeeMaxCount: number | '';
  lateFeeMaxTotal: number | '';
  lateFeeMinOwing: number | '';
  lateFeeChargePartPaid: boolean;
  lateFeeTaxable: boolean;
  lateFeeChargeTuition: boolean;
  lateFeeChargeProducts: boolean;
  lateFeeChargeOther: boolean;
  lateFeeLateInvoiceGapDays: number | '';
  lateFeeCopyBillingContact: boolean;
}

export type { InvoiceSettingsFormData };

/** '' → null, else a number (a cleared box means "none"). */
const optionalNumber = (v: number | ''): number | null => (v === '' || v === null ? null : Number(v));

/** The late-fee half of the PATCH body. */
function lateFeeBody(data: InvoiceSettingsFormData): InvoiceSettingsInput {
  return {
    lateFeeEnabled: data.lateFeeEnabled,
    lateFeeType: data.lateFeeType,
    lateFeeAmount: optionalNumber(data.lateFeeAmount),
    lateFeePercent: optionalNumber(data.lateFeePercent),
    lateFeePerFeeCap: optionalNumber(data.lateFeePerFeeCap),
    lateFeeFirstDays: Number(data.lateFeeFirstDays),
    lateFeeRepeatEnabled: data.lateFeeRepeatEnabled,
    lateFeeRepeatFirstDays: Number(data.lateFeeRepeatFirstDays),
    lateFeeRepeatEveryDays: Number(data.lateFeeRepeatEveryDays),
    lateFeeMaxCount: optionalNumber(data.lateFeeMaxCount),
    lateFeeMaxTotal: optionalNumber(data.lateFeeMaxTotal),
    lateFeeMinOwing: optionalNumber(data.lateFeeMinOwing),
    lateFeeChargePartPaid: data.lateFeeChargePartPaid,
    lateFeeTaxable: data.lateFeeTaxable,
    lateFeeChargeTuition: data.lateFeeChargeTuition,
    lateFeeChargeProducts: data.lateFeeChargeProducts,
    lateFeeChargeOther: data.lateFeeChargeOther,
    lateFeeLateInvoiceGapDays: Number(data.lateFeeLateInvoiceGapDays),
    lateFeeCopyBillingContact: data.lateFeeCopyBillingContact,
  };
}

/** The policy fields of a settings object, for the stricter/looser classifier. */
function policyFields(source: Partial<Record<(typeof LATE_FEE_POLICY_KEYS)[number], unknown>>): LateFeePolicyFields {
  return Object.fromEntries(LATE_FEE_POLICY_KEYS.map((key) => [key, source[key]])) as unknown as LateFeePolicyFields;
}

/** The form's late-fee values from the loaded settings. */
function lateFeeFormValues(settings: InvoiceSettingsResponse) {
  const box = (v: number | null) => (v === null || v === undefined ? ('' as const) : v);
  return {
    lateFeeEnabled: settings.lateFeeEnabled,
    lateFeeType: settings.lateFeeType,
    lateFeeAmount: box(settings.lateFeeAmount),
    lateFeePercent: box(settings.lateFeePercent),
    lateFeePerFeeCap: box(settings.lateFeePerFeeCap),
    lateFeeFirstDays: settings.lateFeeFirstDays,
    lateFeeRepeatEnabled: settings.lateFeeRepeatEnabled,
    lateFeeRepeatFirstDays: settings.lateFeeRepeatFirstDays,
    lateFeeRepeatEveryDays: settings.lateFeeRepeatEveryDays,
    lateFeeMaxCount: box(settings.lateFeeMaxCount),
    lateFeeMaxTotal: box(settings.lateFeeMaxTotal),
    lateFeeMinOwing: box(settings.lateFeeMinOwing),
    lateFeeChargePartPaid: settings.lateFeeChargePartPaid,
    lateFeeTaxable: settings.lateFeeTaxable,
    lateFeeChargeTuition: settings.lateFeeChargeTuition,
    lateFeeChargeProducts: settings.lateFeeChargeProducts,
    lateFeeChargeOther: settings.lateFeeChargeOther,
    lateFeeLateInvoiceGapDays: settings.lateFeeLateInvoiceGapDays,
    lateFeeCopyBillingContact: settings.lateFeeCopyBillingContact,
  };
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function InvoiceSettingsTab() {
  const { can } = usePermissions();
  // Roles slice 1a (V7b): saving needs BOTH the finance write and the settings write.
  const canSave = can('operations_invoices', 'write') && can('system_settings', 'write');
  const { data: settings, isLoading, error } = useInvoiceSettings();
  const updateMutation = useUpdateInvoiceSettings();
  const resetMutation = useResetInvoiceSettings();
  const timeZone = useSchoolTimezone();
  /** #372: the confirm dialog for a late-fee change, and the save it holds back. */
  const [pendingSave, setPendingSave] = useState<{ body: InvoiceSettingsInput; text: LateFeeChangeDescription } | null>(
    null
  );
  /** #372: what the SERVER says the last save did to late fees. */
  const [savedChange, setSavedChange] = useState<LateFeeChangeDescription | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<InvoiceSettingsFormData>({
    defaultValues: {
      prefix: '',
      taxType: 'NONE',
      taxName: '',
      taxRate: 0,
      taxMode: 'NONE',
      taxNumber: '',
      defaultDueDays: 14,
      billingContactEmail: '',
      footerText: '',
      invoiceLineItemGrouping: 'BY_CLASS',
      // #372: MNM's values, off (the server's defaults; replaced as soon as the settings load).
      lateFeeEnabled: false,
      lateFeeType: 'FIXED',
      lateFeeAmount: 25,
      lateFeePercent: '',
      lateFeePerFeeCap: '',
      lateFeeFirstDays: 7,
      lateFeeRepeatEnabled: true,
      lateFeeRepeatFirstDays: 28,
      lateFeeRepeatEveryDays: 28,
      lateFeeMaxCount: '',
      lateFeeMaxTotal: '',
      lateFeeMinOwing: '',
      lateFeeChargePartPaid: false,
      lateFeeTaxable: false,
      lateFeeChargeTuition: true,
      lateFeeChargeProducts: false,
      lateFeeChargeOther: true,
      lateFeeLateInvoiceGapDays: 7,
      lateFeeCopyBillingContact: true,
    },
  });

  // A refused save (LAST_CHANCE_REQUIRED) shows inline; the next edit to any field clears it.
  // Held in a ref: a mutation object is new on every state change and must not sit in effect deps.
  const updateMutationRef = useRef(updateMutation);
  updateMutationRef.current = updateMutation;
  useEffect(() => {
    const subscription = watch((_value, info) => {
      const current = updateMutationRef.current;
      if (info.type === 'change' && current.error) current.reset();
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const taxType = watch('taxType');
  const prefix = watch('prefix');

  // Update form when data loads
  useEffect(() => {
    if (settings) {
      reset({
        prefix: settings.prefix || '',
        taxType: settings.taxType,
        taxName: settings.taxName || '',
        taxRate: settings.taxRate,
        taxMode: settings.taxMode,
        taxNumber: settings.taxNumber || '',
        defaultDueDays: settings.defaultDueDays,
        billingContactEmail: settings.billingContactEmail || '',
        footerText: settings.footerText || '',
        invoiceLineItemGrouping: settings.invoiceLineItemGrouping ?? 'BY_CLASS',
        ...lateFeeFormValues(settings),
      });
    }
  }, [settings, reset]);

  const taxNameFor = (s: { taxType: TaxType; taxName: string | null }) =>
    s.taxName || (s.taxType === 'VAT' ? 'VAT' : s.taxType === 'GST' ? 'GST' : 'tax');

  const save = (body: InvoiceSettingsInput) => {
    // An earlier save's alert must not read as this save's outcome if this one fails.
    setSavedChange(null);
    updateMutation.mutate(body, {
      onSuccess: (result) => {
        // The SERVER's account of what happened (it decides; the dialog only predicted).
        setSavedChange(
          result.lateFeeChange
            ? describeLateFeeChange(result.lateFeeChange, {
                enabledFrom: result.lateFeeChange.enabledFrom,
                taxName: taxNameFor(result),
              })
            : null
        );
      },
    });
  };

  const onSubmit = (data: InvoiceSettingsFormData) => {
    const body: InvoiceSettingsInput = {
      prefix: data.prefix || null,
      taxType: data.taxType,
      taxName: data.taxName || null,
      taxRate: data.taxRate,
      taxMode: data.taxMode,
      taxNumber: data.taxNumber || null,
      defaultDueDays: data.defaultDueDays,
      // Blank means "use the school email" (the server stores null).
      billingContactEmail: data.billingContactEmail.trim() || null,
      footerText: data.footerText || null,
      invoiceLineItemGrouping: data.invoiceLineItemGrouping,
      ...lateFeeBody(data),
    };

    // #372: a change to late fees is CONFIRMED first, in words (the old plan's D14 dialog). The
    // server decides what actually happens and says so after the save.
    if (settings) {
      const next = lateFeeBody(data);
      const change = classifyLateFeeChange(
        { ...policyFields(settings), on: settings.lateFeeEnabled },
        { ...policyFields(next), on: !!next.lateFeeEnabled }
      );
      if (change.kind === 'ENABLED' || change.kind === 'STRICTER' || change.kind === 'DISABLED') {
        const today = schoolTodayUtcMidnight(timeZone).toISOString().slice(0, 10);
        const text = describeLateFeeChange(change, {
          enabledFrom: today,
          taxName: taxNameFor({ taxType: data.taxType, taxName: data.taxName || null }),
        });
        if (text) {
          setPendingSave({ body, text });
          return;
        }
      }
    }
    save(body);
  };

  const handleReset = () => {
    // ⚠️ A reset re-enables family reminder emails and clears the billing contact: say so
    // (/qa of the plan MEDIUM-5, #248).
    if (
      window.confirm(
        'Reset all invoice settings to defaults? This also turns payment reminders back on, clears the billing contact and turns late fees off.'
      )
    ) {
      resetMutation.mutate();
    }
  };

  // Generate sample invoice number
  const currentYear = new Date().getFullYear();
  const samplePrefix = prefix.toUpperCase() || 'MNM';
  const sampleInvoiceNumber = `${samplePrefix}-${currentYear}-00001`;

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load invoice settings. Please try again.
      </Alert>
    );
  }

  return (
    // noValidate: react-hook-form owns validation. Without it the browser's native checks (the
    // billing contact is type="email", the day boxes have min/max) silently block the submit and
    // our messages never show.
    <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {/* Invoice Prefix Section */}
        <Card variant="outlined">
          <CardHeader
            title="Invoice Number Format"
            subheader="Customize the prefix used for invoice numbers"
          />
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="prefix"
                control={control}
                rules={{
                  maxLength: { value: 5, message: 'Max 5 characters' },
                  pattern: { value: /^[A-Za-z0-9]*$/, message: 'Alphanumeric only' },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Invoice Prefix"
                    placeholder="MNM"
                    helperText={fieldState.error?.message || 'Leave blank to use school name initials'}
                    error={!!fieldState.error}
                    inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
                    sx={{ maxWidth: 200 }}
                  />
                )}
              />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Preview:
                </Typography>
                <Chip
                  label={sampleInvoiceNumber}
                  color="primary"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: '1rem' }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Tax Configuration Section */}
        <Card variant="outlined">
          <CardHeader
            title="Tax Configuration"
            subheader="Configure tax settings for invoices"
          />
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="taxType"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <FormLabel>Tax Type</FormLabel>
                    <RadioGroup {...field} row>
                      <FormControlLabel value="NONE" control={<Radio />} label="No Tax" />
                      <FormControlLabel value="GST" control={<Radio />} label="GST (Australia)" />
                      <FormControlLabel value="VAT" control={<Radio />} label="VAT (UK/EU)" />
                      <FormControlLabel value="CUSTOM" control={<Radio />} label="Custom" />
                    </RadioGroup>
                  </FormControl>
                )}
              />

              {taxType !== 'NONE' && (
                <>
                  <Divider />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {taxType === 'CUSTOM' && (
                      <Controller
                        name="taxName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Tax Name"
                            placeholder="Sales Tax"
                            helperText="Display name for the tax"
                            sx={{ minWidth: 200 }}
                          />
                        )}
                      />
                    )}
                    <Controller
                      name="taxRate"
                      control={control}
                      rules={{
                        min: { value: 0, message: 'Min 0%' },
                        max: { value: 100, message: 'Max 100%' },
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="number"
                          label="Tax Rate"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          sx={{ width: 150 }}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      )}
                    />
                    <Controller
                      name="taxNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Tax Registration Number"
                          placeholder="e.g., ABN 12 345 678 901"
                          helperText="Displayed on invoices"
                          sx={{ minWidth: 250 }}
                        />
                      )}
                    />
                  </Stack>

                  <Controller
                    name="taxMode"
                    control={control}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel>Tax Display Mode</FormLabel>
                        <RadioGroup {...field} row>
                          <FormControlLabel
                            value="INCLUDED"
                            control={<Radio />}
                            label="Tax Included (prices include tax)"
                          />
                          <FormControlLabel
                            value="EXCLUDED"
                            control={<Radio />}
                            label="Tax Excluded (add tax on top)"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Payment Terms Section */}
        <Card variant="outlined">
          <CardHeader
            title="Payment Terms"
            subheader="Configure the default payment due date"
          />
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <Controller
                name="defaultDueDays"
                control={control}
                rules={{
                  min: { value: 1, message: 'Min 1 day' },
                  max: { value: 90, message: 'Max 90 days' },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Default Due Days"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'Days after the invoice is created. Used as the due date on new invoices; you can still change it on each one.'
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">days</InputAdornment>,
                    }}
                    inputProps={{ min: 1, max: 90 }}
                    sx={{ width: 180 }}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 14)}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Payment Reminders: the steps (#372 slice B) */}
        <ReminderStepsCard control={control} />

        {/* Late Fees (#372) */}
        {savedChange && (
          <Alert severity={savedChange.severity} onClose={() => setSavedChange(null)}>
            <Typography variant="subtitle2">{savedChange.title}</Typography>
            {savedChange.paragraphs.map((p) => (
              <Typography key={p} variant="body2">
                {p}
              </Typography>
            ))}
          </Alert>
        )}
        <LateFeesCard
          control={control}
          lateFeesActive={settings?.lateFeesActive ?? false}
          savedEnabled={settings?.lateFeeEnabled ?? false}
          enabledFrom={settings?.lateFeesEnabledFrom ?? null}
          taxName={taxNameFor({ taxType: watch('taxType'), taxName: watch('taxName') || null })}
          lastChanceWarning={settings?.lastChanceWarning ?? null}
          saveBlockedMessage={
            apiErrorCode(updateMutation.error) === 'LAST_CHANCE_REQUIRED'
              ? apiErrorMessage(updateMutation.error)
              : null
          }
        />

        {/* Line Item Grouping Section */}
        <Card variant="outlined">
          <CardHeader
            title="Line Item Grouping"
            subheader="Controls how lesson charges are grouped on generated invoices"
          />
          <CardContent>
            <Controller
              name="invoiceLineItemGrouping"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormLabel sx={{ mb: 1 }}>Grouping Mode</FormLabel>
                  <RadioGroup {...field}>
                    <FormControlLabel
                      value="BY_CLASS"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">By Class (Default)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Each lesson creates a separate line item. Hybrid lessons create two items
                            (group sessions + individual sessions).
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="BY_PROGRAM_LEVEL"
                      control={<Radio />}
                      sx={{ mt: 1 }}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">By Program Level</Typography>
                          <Typography variant="caption" color="text.secondary">
                            All lessons sharing the same program level are merged into one line item
                            per student. Hybrid lessons in the same program level are also merged.
                            Lessons without a program level fall back to per-class behavior.
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </CardContent>
        </Card>

        {/* Invoice Footer Section */}
        <Card variant="outlined">
          <CardHeader
            title="Invoice Footer"
            subheader="Custom text displayed at the bottom of invoices"
          />
          <CardContent>
            <Controller
              name="footerText"
              control={control}
              rules={{ maxLength: { value: 500, message: 'Max 500 characters' } }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Thank you for choosing Music 'n Me!"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || `${field.value?.length || 0}/500 characters`}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {canSave && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ResetIcon />}
              onClick={handleReset}
              disabled={resetMutation.isPending}
            >
              Reset to Defaults
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* #372: the late-fee confirm dialog (what this save will do, in words). */}
      <Dialog open={!!pendingSave} onClose={() => setPendingSave(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{pendingSave?.text.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {pendingSave?.text.paragraphs.map((p) => (
              <Typography key={p} variant="body2">
                {p}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingSave(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingSave) save(pendingSave.body);
              setPendingSave(null);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
